import React, { useEffect, useState, useMemo, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminHeader from '../../components/AdminHeader';
import { collection, getDocs } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { db } from '../../firebase';
import './AdminReportsRestaurants.css';
import { exportToPDFArabic } from '../../utils/pdfExporter';
import '../../styles/filters.css';

const AdminReportsRestaurants = () => {
  const [topRestaurants, setTopRestaurants] = useState([]);
  const [topDeliveryFees, setTopDeliveryFees] = useState([]);
  const [topOrderValues, setTopOrderValues] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [restaurantRevenue, setRestaurantRevenue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedOrdersCount, setCompletedOrdersCount] = useState(0);
  const [allOrders, setAllOrders] = useState([]);

  // إضافة حالات للفلاتر
  const [dateRange, setDateRange] = useState('all');
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  
  // Move STATUS_COLORS to useMemo to prevent re-renders
  const STATUS_COLORS = useMemo(() => ({
    'تم التسليم ✅': '#28a745',
    'جاري التوصيل 🚚': '#17a2b8',
    'ملغي ❌': '#dc3545',
    'جديد': '#007bff',
    'غير معروف': '#6c757d'
  }), []);

  // دالة لتطبيق الفلاتر
  const applyFilters = useCallback((orders) => {
    let filtered = [...orders];
    
    // فلترة حسب التاريخ
    if (dateRange !== 'all') {
      const now = new Date();
      filtered = filtered.filter(order => {
        const orderDate = order.createdAt.toDate();
        
        switch (dateRange) {
          case 'today':
            return orderDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.setDate(now.getDate() - 7));
            return orderDate >= weekAgo;
          case 'month':
            return orderDate.getMonth() === now.getMonth() && 
                   orderDate.getFullYear() === now.getFullYear();
          case 'year':
            return orderDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }
    
    // فلترة حسب المطعم
    if (selectedRestaurant !== 'all') {
      filtered = filtered.filter(order => order.restaurantId === selectedRestaurant);
    }
    
    // فلترة حسب الحالة
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(order => order.status === selectedStatus);
    }
    
    return filtered;
  }, [dateRange, selectedRestaurant, selectedStatus]);

  useEffect(() => {
    const fetchRestaurantReports = async () => {
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(db, 'orders'));
        const orders = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setAllOrders(orders);
        
        // تطبيق الفلاتر
        const filteredOrders = applyFilters(orders);
        
        // تحديد الطلبات المكتملة
        const completedOrders = filteredOrders.filter(order => order.status === 'تم التسليم ✅');
        
        console.log('إجمالي عدد الطلبات:', orders.length);
        console.log('عدد الطلبات المفلترة:', filteredOrders.length);
        console.log('عدد الطلبات المكتملة:', completedOrders.length);
        
        setCompletedOrdersCount(completedOrders.length);
  
        // إحصائيات عدد الطلبات لكل مطعم
        const countMap = {};
        // إحصائيات رسوم التوصيل لكل مطعم
        const feeMap = {};
        // إحصائيات قيمة الطلبات لكل مطعم
        const orderValueMap = {};
        // إحصائيات حالة الطلبات
        const statusMap = {};
        // إحصائيات الإيرادات والأرباح لكل مطعم
        const revenueMap = {};
  
        filteredOrders.forEach(order => {
          const name = order.restaurantName || 'غير معروف';
          const id = order.restaurantId || 'unknown';
          const key = `${id}_${name}`;
          const status = order.status || 'غير معروف';
          
          // إجمالي الإيرادات والتفاصيل حسب حالة الطلب
          revenueMap[key] = revenueMap[key] || { 
            name, 
            totalOrders: 0, 
            totalRevenue: 0, 
            completedOrders: 0,
            pendingOrders: 0,
            canceledOrders: 0,
            newOrders: 0,
            deliveryFees: 0,
            netRevenue: 0
          };
          
          revenueMap[key].totalOrders += 1;
          revenueMap[key].totalRevenue += (order.total || 0);
          revenueMap[key].deliveryFees += (order.deliveryFee || 0);
          
          // افتراض أن المطعم يحصل على الإجمالي ناقص رسوم التوصيل والعمولة (15%)
          const commission = (order.total || 0) * 0.15;
          revenueMap[key].netRevenue += (order.total || 0) - (order.deliveryFee || 0) - commission;
          
          // تصنيف الطلبات حسب الحالة
          if (status === 'تم التسليم ✅') {
            revenueMap[key].completedOrders += 1;
          } else if (status === 'جاري التوصيل 🚚') {
            revenueMap[key].pendingOrders += 1;
          } else if (status === 'ملغي ❌') {
            revenueMap[key].canceledOrders += 1;
          } else if (status === 'جديد') {
            revenueMap[key].newOrders += 1;
          }
          
          // عدد الطلبات
          countMap[key] = countMap[key] || { name, count: 0 };
          countMap[key].count += 1;
          
          // رسوم التوصيل
          feeMap[key] = feeMap[key] || { name, totalFee: 0 };
          feeMap[key].totalFee += (order.deliveryFee || 0);
          
          // قيمة الطلبات
          orderValueMap[key] = orderValueMap[key] || { name, totalValue: 0 };
          orderValueMap[key].totalValue += (order.total || 0);
          
          // حالة الطلبات
          statusMap[status] = statusMap[status] || { name: status, value: 0, color: STATUS_COLORS[status] || '#6c757d' };
          statusMap[status].value += 1;
        });
  
        // ترتيب المطاعم حسب عدد الطلبات
        const topCount = Object.values(countMap)
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
  
        // ترتيب المطاعم حسب رسوم التوصيل
        const topFees = Object.values(feeMap)
          .map(item => ({ ...item, totalFee: parseFloat(item.totalFee.toFixed(3)) }))
          .sort((a, b) => b.totalFee - a.totalFee)
          .slice(0, 10);
          
        // ترتيب المطاعم حسب قيمة الطلبات
        const topValues = Object.values(orderValueMap)
          .map(item => ({ ...item, totalValue: parseFloat(item.totalValue.toFixed(3)) }))
          .sort((a, b) => b.totalValue - a.totalValue)
          .slice(0, 10);
          
        // ترتيب المطاعم حسب الإيرادات
        const restaurantRevenueList = Object.values(revenueMap)
          .map(item => ({
            ...item,
            totalRevenue: parseFloat(item.totalRevenue.toFixed(3)),
            netRevenue: parseFloat(item.netRevenue.toFixed(3)),
            deliveryFees: parseFloat(item.deliveryFees.toFixed(3)),
            averageOrderValue: parseFloat((item.totalRevenue / (item.totalOrders || 1)).toFixed(3))
          }))
          .sort((a, b) => b.totalRevenue - a.totalRevenue)
          .slice(0, 10);
  
        setTopRestaurants(topCount);
        setTopDeliveryFees(topFees);
        setTopOrderValues(topValues);
        setOrderStatusData(Object.values(statusMap));
        setRestaurantRevenue(restaurantRevenueList);
        
      } catch (error) {
        console.error('خطأ في جلب البيانات:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchRestaurantReports();
  }, [applyFilters, STATUS_COLORS]);
  
  // دالة تصدير PDF
  const handleExportToPDF = () => {
    const filters = {
      dateRange: dateRange,
      restaurant: selectedRestaurant,
      status: selectedStatus
    };
    
    exportToPDFArabic(restaurantRevenue, filters);
  };

  // الحصول على قائمة المطاعم المتوفرة
  const availableRestaurants = useMemo(() => {
    const restaurants = [...new Set(allOrders.map(order => ({
      id: order.restaurantId,
      name: order.restaurantName
    })))];
    return restaurants.filter(r => r.id && r.name);
  }, [allOrders]);

  if (loading) {
    return (
      <div className="restaurant-dashboard">
        <AdminSidebar />
        <div className="restaurant-dashboard-content">
          <AdminHeader adminName="تقارير المطاعم" />
          <div className="loading-container">
            <div className="spinner"></div>
            <p>جاري تحميل البيانات...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="restaurant-dashboard">
        <AdminSidebar />
        <div className="restaurant-dashboard-content">
          <AdminHeader adminName="تقارير المطاعم" />
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="restaurant-dashboard">
      <AdminSidebar />
      <div className="restaurant-dashboard-content">
        <AdminHeader adminName="تقارير المطاعم" />

        <div className="restaurant-dashboard-welcome">
          <h1>تقارير المطاعم</h1>
          <p>الرسوم البيانية والإحصائيات الخاصة بالمطاعم</p>
        </div>

        {/* الفلاتر */}
        <div className="filters-section">
          <div className="filters-group">
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="all">جميع الفترات</option>
              <option value="today">اليوم</option>
              <option value="week">هذا الأسبوع</option>
              <option value="month">هذا الشهر</option>
              <option value="year">هذه السنة</option>
            </select>
            
            <select value={selectedRestaurant} onChange={(e) => setSelectedRestaurant(e.target.value)}>
              <option value="all">جميع المطاعم</option>
              {availableRestaurants.map(restaurant => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
            
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              <option value="all">جميع الحالات</option>
              <option value="تم التسليم ✅">تم التسليم</option>
              <option value="جاري التوصيل 🚚">جاري التوصيل</option>
              <option value="ملغي ❌">ملغي</option>
              <option value="جديد">جديد</option>
            </select>
            
            <button onClick={handleExportToPDF} className="export-btn pdf">
              تصدير PDF
              <i className="fas fa-file-pdf"></i>
            </button>
          </div>
        </div>

        <div className="statistics-summary">
          <div className="stat-card">
            <h4>إجمالي المطاعم</h4>
            <p>{restaurantRevenue.length}</p>
          </div>
          <div className="stat-card">
            <h4>إجمالي الطلبات</h4>
            <p>{restaurantRevenue.reduce((sum, item) => sum + item.totalOrders, 0)}</p>
          </div>
          <div className="stat-card">
            <h4>الطلبات المكتملة</h4>
            <p>{completedOrdersCount}</p>
          </div>
          <div className="stat-card">
            <h4>إجمالي قيمة الطلبات</h4>
            <p>{restaurantRevenue.reduce((sum, item) => sum + item.totalRevenue, 0).toFixed(3)} د.ك</p>
          </div>
        </div>

        {/* باقي المحتوى كما هو */}
        {/* حالة الطلبات */}
        <div className="report-chart-section">
          <h3>توزيع الطلبات حسب الحالة</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={orderStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip formatter={(value) => [`${value} طلب`, 'العدد']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* باقي الرسومات البيانية والجداول */}
        <div className="report-chart-section">
          <h3>صافي أرباح المطاعم بعد خصم العمولة ورسوم التوصيل (د.ك)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={restaurantRevenue} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} د.ك`} />
              <Bar dataKey="netRevenue" fill="#4CAF50" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="report-chart-section">
          <h3>أكثر المطاعم من حيث قيمة الطلبات (د.ك)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topOrderValues} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} د.ك`} />
              <Bar dataKey="totalValue" fill="#28a745" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="report-chart-section">
          <h3>أكثر المطاعم من حيث رسوم التوصيل (د.ك)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topDeliveryFees} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} د.ك`} />
              <Bar dataKey="totalFee" fill="#17a2b8" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="report-chart-section">
          <h3>أكثر المطاعم نشاطًا (حسب عدد الطلبات)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topRestaurants} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} طلب`} />
              <Bar dataKey="count" fill="#4a90e2" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="detailed-table">
          <h3>تفاصيل الإيرادات والأرباح للمطاعم</h3>
          <table>
            <thead>
              <tr>
                <th>المطعم</th>
                <th>عدد الطلبات</th>
                <th>مكتملة</th>
                <th>قيد التوصيل</th>
                <th>ملغية</th>
                <th>إجمالي الإيرادات (د.ك)</th>
                <th>رسوم التوصيل (د.ك)</th>
                <th>صافي الأرباح (د.ك)</th>
                <th>متوسط قيمة الطلب (د.ك)</th>
              </tr>
            </thead>
            <tbody>
              {restaurantRevenue.map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>{item.totalOrders}</td>
                  <td>{item.completedOrders}</td>
                  <td>{item.pendingOrders}</td>
                  <td>{item.canceledOrders}</td>
                  <td>{item.totalRevenue}</td>
                  <td>{item.deliveryFees}</td>
                  <td>{item.netRevenue}</td>
                  <td>{item.averageOrderValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReportsRestaurants;